import './User.css';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/AuthService';
import PasswordField from './passwordField/PasswordField';

// The profile: one form covering everything about the account. The password lives in it like any
// other field — shown as a placeholder while reading, and as "Current Password" plus "New Password"
// while editing. Leaving both password boxes empty keeps the password as it is, so Save works the
// same whether or not someone is changing it.
//
// Username and email address are deliberately not editable. Changing an address would need its own
// confirmation step, or it becomes a way to take an account over.

// Stands in for the password while reading. Never the real one — the API does not return it and
// could not, since it only stores a hash.
const HIDDEN_PASSWORD = '••••••••';

/** The messages out of a DRF error body ({"field": ["..."], ...}), in one flat list. */
function messagesFrom(data) {
    const messages = [];
    for (const value of Object.values(data || {})) {
        if (Array.isArray(value)) messages.push(...value.filter((item) => typeof item === 'string'));
        else if (typeof value === 'string') messages.push(value);
    }
    return messages.length ? messages : ['That did not work. Please try again.'];
}

export default function User() {

    const [userInfo, setUserInfo] = useState({});
    const [newUserInfo, setNewUserInfo] = useState(userInfo);
    const [checkedAuth, setCheckedAuth] = useState(false);
    const [editingUserInfo, setEditingUserInfo] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState('');
    const navigate = useNavigate();
    const firstRender = useRef(true);

    // Make request to get user's info. If they are not logged in, redirect to login page.
    useEffect(() => {
        const getUserInfo = async () => {
            const userInfo = await AuthService.makeRequest({ urlExtension: 'user/info/', method: 'GET', body: null });

            if (userInfo.error) {
                navigate('/user/login');
            } else {
                setUserInfo(userInfo.data);
                setNewUserInfo(userInfo.data);
                setCheckedAuth(true);
            }
        }

        if (firstRender.current) {
            firstRender.current = false;
            getUserInfo();
        }

    }, [navigate])

    const changingPassword = Boolean(currentPassword || newPassword);

    const startEditing = () => {
        setSaved('');
        setErrors([]);
        setEditingUserInfo(true);
    };

    const stopEditing = () => {
        setEditingUserInfo(false);
        setNewUserInfo(userInfo);
        setCurrentPassword('');
        setNewPassword('');
        setErrors([]);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setErrors([]);
        setSaved('');

        if (changingPassword && !(currentPassword && newPassword)) {
            setErrors(['To change your password, fill in both boxes. Leave them both empty to keep the one you have.']);
            return;
        }

        setSaving(true);
        const profile = await AuthService.makeRequest({ urlExtension: 'user/info/', method: 'PUT', body: newUserInfo });

        if (profile.error) {
            setSaving(false);
            // No body at all means the session is gone rather than the form being wrong.
            if (!profile.data) { navigate('/user/login'); return; }
            setErrors(messagesFrom(profile.data));
            return;
        }

        if (changingPassword) {
            const changed = await AuthService.makeRequest({
                urlExtension: 'user/password/change/',
                method: 'POST',
                body: { current_password: currentPassword, new_password: newPassword },
            });

            if (changed.error) {
                setSaving(false);
                if (!changed.data) { navigate('/user/login'); return; }
                // The profile half already saved, so keep it on screen and only report the password.
                setUserInfo(profile.data);
                setNewUserInfo(profile.data);
                setErrors(messagesFrom(changed.data));
                return;
            }

            // The change ended every session, this one included; keep the credentials handed back or
            // the next request would be rejected.
            if (changed.data?.access && changed.data?.refresh) {
                const { access, refresh } = changed.data;
                try {
                    sessionStorage.setItem('tokens', JSON.stringify({ access, refresh }));
                } catch {
                    // A browser that refuses storage just means signing in again; the change still happened.
                }
            }
        }

        setSaving(false);
        setUserInfo(profile.data);
        setNewUserInfo(profile.data);
        setCurrentPassword('');
        setNewPassword('');
        setEditingUserInfo(false);
        setSaved(changingPassword ? 'Your profile and password have been updated.' : 'Your profile has been updated.');
    };

    if (!checkedAuth) {
        return null;
    }

    return (
        <main className='user-main'>
            <h2 className='user-title'>My Profile</h2>
            {saved ? <p className='user-saved'>{saved}</p> : null}

            {editingUserInfo ? (
                <form onSubmit={handleSave}>
                    <section className='user-content'>
                        <label className='user-field'>
                            <div className='user-field-title'>Username</div>
                            <div className='user-field-content'><input className='user-field-edit' type='text' value={newUserInfo.username} disabled /></div>
                        </label>
                        <label className='user-field'>
                            <div className='user-field-title'>Email Address</div>
                            <div className='user-field-content'><input className='user-field-edit' type='text' value={newUserInfo.email} disabled /></div>
                        </label>
                        <label className='user-field'>
                            <div className='user-field-title'>First Name</div>
                            <div className='user-field-content'><input className='user-field-edit' type='text' value={newUserInfo.first_name} onChange={(e) => setNewUserInfo({...newUserInfo, first_name: e.target.value})} /></div>
                        </label>
                        <label className='user-field'>
                            <div className='user-field-title'>Last Name</div>
                            <div className='user-field-content'><input className='user-field-edit' type='text' value={newUserInfo.last_name} onChange={(e) => setNewUserInfo({...newUserInfo, last_name: e.target.value})} /></div>
                        </label>

                        {/* Written in the order a single column should read: the birthdate, then the
                            two password boxes together. On a wide screen User.css puts Current Password
                            back beside the birthdate and drops New Password onto the row below. */}
                        <label className='user-field user-field-birthdate'>
                            <div className='user-field-title'>Birthdate</div>
                            <div className='user-field-content'><input className='user-field-edit' type='text' value={newUserInfo.birthdate} onChange={(e) => setNewUserInfo({...newUserInfo, birthdate: e.target.value})} /></div>
                        </label>
                        <PasswordField label='Current Password' value={currentPassword} className='user-field-password'
                                       onChange={setCurrentPassword} autoComplete='current-password' />
                        <PasswordField label='New Password' value={newPassword} className='user-field-new-password'
                                       onChange={setNewPassword} autoComplete='new-password' />
                    </section>

                    {changingPassword ?
                        <p className='user-note'>Changing your password signs you out everywhere else.</p> : null}

                    {errors.length > 0 &&
                        <ul className='user-errors'>
                            {errors.map((message) => (
                                <li key={message}><i className="fa-solid fa-circle-info"></i> {message}</li>
                            ))}
                        </ul>
                    }

                    <div className='user-edit-btns'>
                        <button className='user-edit-btn' type='submit' disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button className='user-edit-btn' type='button' onClick={stopEditing}>
                            Cancel
                        </button>
                    </div>
                </form>
            ) : (
                <>
                    <section className='user-content'>
                        <label className='user-field'>
                            <div className='user-field-title'>Username</div>
                            <div className='user-field-content'>{userInfo.username ? userInfo.username : 'N/A'}</div>
                        </label>
                        <label className='user-field'>
                            <div className='user-field-title'>Email Address</div>
                            <div className='user-field-content'>{userInfo.email ? userInfo.email : 'N/A'}</div>
                        </label>
                        <label className='user-field'>
                            <div className='user-field-title'>First Name</div>
                            <div className='user-field-content'>{userInfo.first_name ? userInfo.first_name : 'N/A'}</div>
                        </label>
                        <label className='user-field'>
                            <div className='user-field-title'>Last Name</div>
                            <div className='user-field-content'>{userInfo.last_name ? userInfo.last_name : 'N/A'}</div>
                        </label>
                        <label className='user-field user-field-birthdate'>
                            <div className='user-field-title'>Birthdate</div>
                            <div className='user-field-content'>{userInfo.birthdate ? userInfo.birthdate : 'N/A'}</div>
                        </label>
                        <label className='user-field user-field-password'>
                            <div className='user-field-title'>Password</div>
                            <div className='user-field-content'>{HIDDEN_PASSWORD}</div>
                        </label>
                    </section>
                    <div className='user-edit-btns'>
                        <button className='user-edit-btn' onClick={startEditing}>
                            Edit
                        </button>
                    </div>
                </>
            )}
        </main>
    );
}
